export type Language = "th" | "en" | "ru" | "my" | "vi";
export type TranslationPreferences = {
  tone: "preserve" | "warm" | "neutral";
  formality: "preserve" | "casual" | "polite";
  literalness: "balanced" | "natural" | "literal";
};

export type MiniAppRole = "creator" | "chief" | "participant" | "stranger";

export type Me = {
  userId: number;
  username?: string;
  firstName?: string;
  role: MiniAppRole;
  isCreator: boolean;
  isChief: boolean;
  startScope: {
    businessConnectionId: string;
    chatId: number;
  } | null;
  balance: {
    spentMicrousd: number;
    allocatedMicrousd: number;
  } | null;
};

export type Defaults = {
  translationPreferences: TranslationPreferences;
  translationInstructions: string;
  understoodLanguages: Language[];
  retentionDays: number | null;
  configVersion: number;
};

export type Conversation = {
  businessConnectionId: string;
  chatId: number;
  peer: {
    displayName: string | null;
    username: string | null;
  };
  active: boolean;
  ownerSourceLanguages: Language[];
  partnerSourceLanguages: Language[];
  ownerTargetLanguage: Language;
  partnerTargetLanguage: Language;
  debounceMs: number;
  historyWindow: number;
  keyterms?: string[];
  translationPreferences: {
    effective: TranslationPreferences;
    override: Partial<TranslationPreferences>;
  };
  translationInstructions?: {
    inherited: string;
    override: string | null;
  };
  retentionDays: {
    effective: number | null;
    inherited: number | null;
    override: number | null;
  };
  configVersion: number;
};

export type ConversationDetail = {
  conversation: Conversation;
};

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly payload: Record<string, unknown>,
  ) {
    super(typeof payload.error === "string" ? payload.error : `request_${status}`);
    this.name = "ApiError";
  }
}

function initData(): string {
  return window.Telegram?.WebApp?.initData ?? "";
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    cache: "no-store",
    headers: {
      authorization: `tma ${initData()}`,
      ...(init.body ? { "content-type": "application/json" } : {}),
      ...init.headers,
    },
  });
  const payload = await response.json().catch(() => ({})) as Record<string, unknown>;
  if (!response.ok) throw new ApiError(response.status, payload);
  return payload as T;
}

const scopePath = (conversation: Pick<Conversation, "businessConnectionId" | "chatId">): string =>
  `/api/v1/conversations/${encodeURIComponent(conversation.businessConnectionId)}/${conversation.chatId}`;

export const api = {
  hasInitData: (): boolean => initData().length > 0,
  me: async (): Promise<Me> => request<Me>("/api/v1/me"),
  defaults: async (): Promise<Defaults> =>
    (await request<{ defaults: Defaults }>("/api/v1/defaults")).defaults,
  conversations: async (): Promise<Conversation[]> =>
    (await request<{ conversations: Conversation[] }>("/api/v1/conversations")).conversations,
  conversation: async (
    scope: Pick<Conversation, "businessConnectionId" | "chatId">,
  ): Promise<ConversationDetail> => request<ConversationDetail>(scopePath(scope)),
  patchDefaults: async (
    patch: Partial<Omit<Defaults, "configVersion">> & { configVersion: number },
  ): Promise<Defaults> =>
    (await request<{ defaults: Defaults }>("/api/v1/defaults", {
      method: "PATCH",
      body: JSON.stringify(patch),
    })).defaults,
  patchConversation: async (
    conversation: Pick<Conversation, "businessConnectionId" | "chatId">,
    patch: Record<string, unknown> & { configVersion: number },
  ): Promise<ConversationDetail> => request<ConversationDetail>(scopePath(conversation), {
    method: "PATCH",
    body: JSON.stringify(patch),
  }),
  reflect: async (
    conversation: Pick<Conversation, "businessConnectionId" | "chatId">,
    question: string,
    thread: ReadonlyArray<{ role: "asker" | "bot"; text: string }> = [],
  ): Promise<{ answer: string }> =>
    request(`${scopePath(conversation)}/reflect`, {
      method: "POST",
      body: JSON.stringify({ question, ...(thread.length > 0 ? { thread } : {}) }),
    }),
  notes: async (
    conversation: Pick<Conversation, "businessConnectionId" | "chatId">,
  ): Promise<{ lines: string[] }> =>
    request(`${scopePath(conversation)}/notes`),
  keepNote: async (
    conversation: Pick<Conversation, "businessConnectionId" | "chatId">,
    body: string,
  ): Promise<{ id: number }> =>
    request(`${scopePath(conversation)}/notes`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
};
